import { takeEvery, fork, put, all, call } from "redux-saga/effects";
import { message } from "antd";
import {
  getMappingListSuccess,
  getAgentsListSuccess,
  getExistingMappingsSuccess,
  getAgentByIdSuccess,
  saveMappingSuccess,
  createTelecallerSuccess,
  updateTelecallerSuccess,
  deleteTelecallerSuccess,
  deleteMappingSuccess,
  updateMappingStatusSuccess,
  apiError,
} from "./samparkTelecallersSlice";
import { getList, postAdd, postDelete, patchEdit } from "../../../helpers/Helper";

function* getMappingListSaga(action) {
  try {
    // Using the sampark agents endpoint to get the list with params
    const params = action.payload || {};
    const response = yield call(
      getList,
      "/thirdparty/sampark/agents/",
      { params }
    );
    yield put(getMappingListSuccess(response));
  } catch (error) {
    yield put(apiError(error));
    // Handle different error response formats
    const errorMsg = error?.response?.data?.desc || 
                     error?.response?.data?.error || 
                     error?.response?.data?.message ||
                     error?.error?.message || 
                     error?.message || 
                     error.error[0]  ||
                     "Failed to load mapping data";
    message.error(errorMsg);
  }
}

function* getExistingMappingsSaga(action) {
  try {
    // Get existing agent-user mappings
    // If agent is provided, filter by agent
    const params = action.payload || {};
    const response = yield call(getList, "/masters/samparkagentmapping/", { params });
    yield put(getExistingMappingsSuccess(response));
  } catch (error) {
    yield put(apiError(error));
    console.error("Failed to load existing mappings:", error);
  }
}

function* getAgentByIdSaga(action) {
  try {
    const id = action.payload;
    const response = yield call(getList, `/thirdparty/sampark/agents/${id}/`, {});
    yield put(getAgentByIdSuccess(response));
  } catch (error) {
    yield put(apiError(error));
    // Handle different error response formats
    const errorMsg = error?.response?.data?.desc || 
                     error?.response?.data?.error || 
                     error?.response?.data?.message ||
                     error?.error?.message || 
                     error?.message || 
                     error.error[0]  ||
                     "Failed to load agent details";
    message.error(errorMsg);
  }
}

function* saveMappingDataSaga(action) {
  try {
    const mappings = action.payload;
    
    // If the API supports bulk creation, send all at once
    // Otherwise, send individual requests
    if (Array.isArray(mappings)) {
      let successCount = 0;
      let failCount = 0;
      let errorMessages = [];
      let agentId = null; // Track agent_id for reloading
      
      // Extract agent_id before processing (even if save fails, we need it for reload)
      if (mappings.length > 0 && mappings[0].agent_id) {
        agentId = mappings[0].agent_id;
      }
      
      for (const mapping of mappings) {
        try {
          yield call(
            postAdd,
            "/masters/samparkagentmapping/",
            mapping
          );
          successCount++;
        } catch (error) {
          failCount++;
          // Extract error message from response - handle different formats
          const errorMsg = error?.response?.data?.desc || 
                          error?.response?.data?.error || 
                          error?.response?.data?.message ||
                          error?.error?.message || 
                          error?.message || 
                          error.error[0]  ||
                          "Unknown error";
          errorMessages.push(errorMsg);
          console.error("Failed to save mapping:", mapping, error);
        }
      }
      
      yield put(saveMappingSuccess());
      
      if (successCount > 0 && failCount === 0) {
        message.success(`Successfully saved ${successCount} mapping(s)!`);
      } else if (successCount > 0 && failCount > 0) {
        message.warning(`Saved ${successCount} mapping(s), ${failCount} failed`);
        // Show first error message
        if (errorMessages.length > 0) {
          message.error(errorMessages[0]);
        }
      } else {
        // Show first error message
        if (errorMessages.length > 0) {
          message.error(errorMessages[0]);
        } else {
          message.error("Failed to save mappings");
        }
      }
      
      // Reload the mapping list after save
      yield put({ type: "samparkTelecallers/getMappingList", payload: {} });
      // ALWAYS reload existing mappings with agent filter to maintain filtered view
      if (agentId) {
        yield put({ type: "samparkTelecallers/getExistingMappings", payload: { agent: agentId } });
      }
    } else {
      // Single mapping - extract agent_id before trying to save
      const agentId = mappings.agent_id;
      
      yield call(
        postAdd,
        "/masters/samparkagentmapping/",
        mappings
      );
      yield put(saveMappingSuccess());
      message.success("Mapping saved successfully!");
      yield put({ type: "samparkTelecallers/getMappingList", payload: {} });
      // ALWAYS reload with agent filter to maintain filtered view
      if (agentId) {
        yield put({ type: "samparkTelecallers/getExistingMappings", payload: { agent: agentId } });
      }
    }
  } catch (error) {
    yield put(apiError(error));
    // Extract error message from API response - handle different formats
    const errorMsg = error?.response?.data?.desc || 
                     error?.response?.data?.error || 
                     error?.response?.data?.message ||
                     error?.error?.message || 
                     error?.message || 
                     error.error[0]  ||
                     "Failed to save mapping data";
    message.error(errorMsg);
    
    // IMPORTANT: Reload with agent filter even on error to maintain filtered view
    const mappings = action.payload;
    const agentId = Array.isArray(mappings) 
      ? (mappings.length > 0 ? mappings[0].agent_id : null)
      : mappings.agent_id;
    
    if (agentId) {
      yield put({ type: "samparkTelecallers/getExistingMappings", payload: { agent: agentId } });
    }
  }
}

function* createTelecallerSaga(action) {
  try {
    const telecallerData = action.payload;
    // Only send agent_name and agent_login_id as per API requirement
    const requestData = {
      agent_name: telecallerData.agent_name,
      agent_login_id: telecallerData.agent_login_id,
    };
    yield call(
      postAdd,
      "/thirdparty/sampark/agents/",
      requestData
    );
    yield put(createTelecallerSuccess());
    message.success("Telecaller created successfully!");
    // Reload the list after creation
    yield put({ type: "samparkTelecallers/getMappingList", payload: {} });
  } catch (error) {
    yield put(apiError(error));
    // Handle different error response formats
    const errorMsg = error?.response?.data?.desc || 
                     error?.response?.data?.error || 
                     error?.response?.data?.message ||
                     error?.error?.message || 
                     error?.message || 
                     error.error[0]  ||
                     "Failed to create telecaller";
    message.error(errorMsg);
  }
}

function* updateTelecallerSaga(action) {
  try {
    const { id, ...updateData } = action.payload;
    yield call(
      patchEdit,
      `/thirdparty/sampark/agents/${id}/`,
      updateData
    );
    yield put(updateTelecallerSuccess());
    message.success("Telecaller updated successfully!");
    // Reload the list after update
    yield put({ type: "samparkTelecallers/getMappingList", payload: {} });
  } catch (error) {
    yield put(apiError(error));
    // Handle different error response formats
    const errorMsg = error?.response?.data?.desc || 
                     error?.response?.data?.error || 
                     error?.response?.data?.message ||
                     error?.error?.message || 
                     error?.message || 
                     error.error[0]  ||
                     "Failed to update telecaller";
    message.error(errorMsg);
  }
}

function* deleteTelecallerSaga(action) {
  try {
    const id = action.payload;
    yield call(postDelete, `/thirdparty/sampark/agents/${id}/`, {});
    yield put(deleteTelecallerSuccess());
    message.success("Telecaller deleted successfully!");
    // Reload the list after delete
    yield put({ type: "samparkTelecallers/getMappingList", payload: {} });
  } catch (error) {
    yield put(apiError(error));
    // Handle different error response formats
    const errorMsg = error?.response?.data?.desc || 
                     error?.response?.data?.error || 
                     error?.response?.data?.message ||
                     error?.error?.message || 
                     error?.message || 
                     error.error[0]  ||
                     "Failed to delete telecaller";
    message.error(errorMsg);
  }
}

function* deleteMappingSaga(action) {
  try {
    const { mappingId, agentId } = action.payload;
    // Delete the mapping using the mapping ID
    yield call(postDelete, `/masters/samparkagentmapping/${mappingId}`, {});
    yield put(deleteMappingSuccess());
    message.success("User mapping removed successfully!");
    // Reload the lists after delete
    yield put({ type: "samparkTelecallers/getMappingList", payload: {} });
    // ALWAYS reload with agent filter to maintain filtered view
    if (agentId) {
      yield put({ type: "samparkTelecallers/getExistingMappings", payload: { agent: agentId } });
    }
  } catch (error) {
    yield put(apiError(error));
    // Handle different error response formats
    const errorMsg = error?.response?.data?.desc || 
                     error?.response?.data?.error || 
                     error?.response?.data?.message ||
                     error?.error?.message || 
                     error?.message || 
                     error.error[0]  ||
                     "Failed to remove mapping";
    message.error(errorMsg);
    
    // IMPORTANT: Reload with agent filter even on error to maintain filtered view
    const { agentId } = action.payload;
    if (agentId) {
      yield put({ type: "samparkTelecallers/getExistingMappings", payload: { agent: agentId } });
    }
  }
}

function* updateMappingStatusSaga(action) {
  try {
    const { id, is_active, agentId } = action.payload;
    // yield call(patchEdit, `/masters/samparkagentmapping/${id}`, { is_active });
    yield call(patchEdit, `/masters/samparkagentmapping/${id}/inactivate/`, { is_active });
    yield put(updateMappingStatusSuccess());
    message.success(`Mapping ${is_active ? 'activated' : 'deactivated'} successfully!`);
    // Reload the lists after update
    yield put({ type: "samparkTelecallers/getMappingList", payload: {} });
    // ALWAYS reload with agent filter to maintain filtered view
    if (agentId) {
      yield put({ type: "samparkTelecallers/getExistingMappings", payload: { agent: agentId } });
    }
  } catch (error) {
    yield put(apiError(error));
    // Handle different error response formats
    console.log(error.error[0],"sagaaaaaaaaaaaaa")
    const errorMsg = error?.response?.data?.desc || 
                     error?.response?.data?.error || 
                     error?.response?.data?.message ||
                     error.error[0]  ||
                     error?.message || 
                     error?.error?.message || 
                     "Failed to update mapping status";
    message.error(errorMsg);
    
    // IMPORTANT: Reload with agent filter even on error to maintain filtered view
    const { agentId } = action.payload;
    if (agentId) {
      yield put({ type: "samparkTelecallers/getExistingMappings", payload: { agent: agentId } });
    }
  }
}

export function* watchGetMappingList() {
  yield takeEvery("samparkTelecallers/getMappingList", getMappingListSaga);
}

export function* watchGetAgentsList() {
  yield takeEvery("samparkTelecallers/getAgentsList", getMappingListSaga);
}



export function* watchGetExistingMappings() {
  yield takeEvery("samparkTelecallers/getExistingMappings", getExistingMappingsSaga);
}

export function* watchGetAgentById() {
  yield takeEvery("samparkTelecallers/getAgentById", getAgentByIdSaga);
}

export function* watchSaveMappingData() {
  yield takeEvery("samparkTelecallers/saveMappingData", saveMappingDataSaga);
}

export function* watchCreateTelecaller() {
  yield takeEvery("samparkTelecallers/createTelecaller", createTelecallerSaga);
}

export function* watchUpdateTelecaller() {
  yield takeEvery("samparkTelecallers/updateTelecaller", updateTelecallerSaga);
}

export function* watchDeleteTelecaller() {
  yield takeEvery("samparkTelecallers/deleteTelecaller", deleteTelecallerSaga);
}

export function* watchDeleteMapping() {
  yield takeEvery("samparkTelecallers/deleteMapping", deleteMappingSaga);
}

export function* watchUpdateMappingStatus() {
  yield takeEvery("samparkTelecallers/updateMappingStatus", updateMappingStatusSaga);
}

function* samparkTelecallersSaga() {
  yield all([
    fork(watchGetMappingList),
    fork(watchGetAgentsList),
    fork(watchGetExistingMappings),
    fork(watchGetAgentById),
    fork(watchSaveMappingData),
    fork(watchCreateTelecaller),
    fork(watchUpdateTelecaller),
    fork(watchDeleteTelecaller),
    fork(watchDeleteMapping),
    fork(watchUpdateMappingStatus),
  ]);
}

export default samparkTelecallersSaga;
